import raw68Lessons from "../../../docs/6-8-lessons.json";
import type { MindUPLesson } from "./k-2";
import {
  buildLessonSourceText,
  chooseFirstNonEmpty,
  cleanCurriculumText,
  deriveStructuredSections,
  isLikelyContaminatedList,
  normalizeActivities,
  sanitizeBrainLinkText,
  sanitizePortfolioText,
  sanitizePurposeText,
  splitLiteratureItems,
  splitListItems,
} from "./normalize-raw";

type RawLesson = Partial<MindUPLesson> & {
  id?: string;
  content?: Record<string, unknown>;
};

const LESSON_TITLE_FALLBACKS: Record<number, string> = {
  1: "Building the MindUP Learning Community",
  2: "Understanding the Brain",
  3: "The Brain Break",
  4: "Mindful Awareness in Practice: Using Our Senses",
  5: "Mindful Awareness in the Body",
  6: "The Adolescent Brain",
  7: "Getting Granular about Feelings",
  8: "Empathy: Relationship Builder",
  9: "Fostering an Optimistic View",
  10: "Practicing Gratitude",
  11: "Acts of Kindness and Compassion",
  12: "Taking Care of Me",
  13: "We Are All Connected",
  14: "Mindful Consumption",
  15: "Taking Action in Our Community",
};

function normalizeLesson(rawLesson: RawLesson): MindUPLesson {
  const content = rawLesson.content ?? {};
  const maybeFollowingUp = (content.followingUp ?? {}) as Record<string, unknown>;
  const sourceText = buildLessonSourceText(rawLesson);
  const sections = deriveStructuredSections(sourceText);
  const rawConcepts = splitListItems(rawLesson.concepts);
  const rawObjectives = splitListItems(rawLesson.objectives);
  const rawSel = splitListItems(rawLesson.socialEmotionalLearning);
  const rawMaterials = splitListItems(rawLesson.materials);

  const unit = typeof rawLesson.unit === "number" ? rawLesson.unit : 1;
  const lessonNumber = typeof rawLesson.lessonNumber === "number" ? rawLesson.lessonNumber : 1;
  const id = cleanCurriculumText(rawLesson.id) || `g6-8-u${unit}-l${lessonNumber}`;

  const parsedTitle = cleanCurriculumText(rawLesson.title);
  const title = parsedTitle && parsedTitle !== "| G 6-8" ? parsedTitle : LESSON_TITLE_FALLBACKS[lessonNumber] ?? `Lesson ${lessonNumber}`;

  return {
    id,
    unit,
    unitTitle: cleanCurriculumText(rawLesson.unitTitle) || `Unit ${unit}`,
    lessonNumber,
    gradeLevel: "6-8",
    title,
    purpose: sanitizePurposeText(rawLesson.purpose),
    concepts: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawConcepts) ? [] : rawConcepts,
      sections.concepts,
    ) ?? [],
    objectives: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawObjectives) ? [] : rawObjectives,
      sections.objectives,
    ) ?? [],
    socialEmotionalLearning: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawSel) ? [] : rawSel,
      sections.socialEmotionalLearning,
    ) ?? [],
    materials: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawMaterials) ? [] : rawMaterials,
      sections.materials,
    ) ?? [],
    estimatedTime: cleanCurriculumText(rawLesson.estimatedTime) || "30-45 minutes",
    content: {
      brainLink:
        chooseFirstNonEmpty(
          sanitizeBrainLinkText(content.brainLink),
          sanitizeBrainLinkText(sections.brainLink),
        ) ?? "",
      gettingStarted: chooseFirstNonEmpty(splitListItems(content.gettingStarted), sections.gettingStarted) ?? [],
      activities: normalizeActivities(content.activities, title),
      makingConnections: chooseFirstNonEmpty(splitListItems(content.makingConnections), sections.makingConnections) ?? [],
      followingUp: {
        mindupForHomeAndLife:
          chooseFirstNonEmpty(splitListItems(maybeFollowingUp.mindupForHomeAndLife), sections.mindupForHomeAndLife) ?? [],
        mindfulMePortfolio:
          chooseFirstNonEmpty(
            sanitizePortfolioText(maybeFollowingUp.mindfulMePortfolio),
            sanitizePortfolioText(sections.mindfulMePortfolio),
          ) ?? "",
      },
      simplificationIdeas: chooseFirstNonEmpty(splitListItems(content.simplificationIdeas), sections.simplificationIdeas) ?? [],
      challengerIdeas: chooseFirstNonEmpty(splitListItems(content.challengerIdeas), sections.challengerIdeas) ?? [],
      literatureList:
        chooseFirstNonEmpty(
          splitLiteratureItems(content.literatureList),
          sections.literatureList,
        ) ?? [],
    },
  };
}

export const G68_CURRICULUM: MindUPLesson[] = (raw68Lessons as RawLesson[]).map(normalizeLesson);
